import { Container, Col, Row } from 'react-bootstrap';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import BreadcrumbStructuredData from '@/components/structured_data/BreadcrumbStructuredData';
import { useRouter } from 'next/router';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

export default function DataPrivacy() {
  const { t } = useTranslation('other');
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>{t('data_privacy.meta_title')}</title>
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <BreadcrumbStructuredData
          items={[
            { name: 'Home', item: 'https://spiritory.com' },
            {
              name: translationFallback(
                'data_privacy.privacy_policy',
                t('data_privacy.privacy_policy'),
                'Privacy policy'
              ),
              item: canonicalUrl,
            },
          ]}
        />
      </Head>
      <Container className="p-5">
        <Row
          className="bg-primary text-white rounded"
          style={{
            width: '60%',
            minWidth: 900,
            padding: '1.5rem 4rem',
            margin: '0 auto',
          }}
        >
          <Row>
            <Col className="d-flex justify-content-center align-items-center">
              <h1>
                {translationFallback(
                  'data_privacy.privacy_policy',
                  t('data_privacy.privacy_policy'),
                  'Privacy policy'
                )}
              </h1>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex flex-column gap-3">
              <div>
                <p>
                  {translationFallback(
                    'data_privacy.p_1',
                    t('data_privacy.p_1'),
                    'Responsible within the meaning of data protection laws, especially the EU General Data Protection Regulation (GDPR), is:'
                  )}
                  <br />
                  <br />
                  Spiritory GmbH Oberländerstr. 33 81371{' '}
                  {translationFallback('munich', tCommon('munich'), 'Munich')}
                  <br />
                  <br />
                  {translationFallback('phone_number', tCommon('phone_number'), 'Phone number')}:
                  +49 (0) 89 69313426 &nbsp;Email:&nbsp;
                  <a className="text-white" href="mailto:help@spiritory.com">
                    help@spiritory.com
                  </a>
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.h_1',
                    t('data_privacy.h_1'),
                    'I. Your rights as the data subject'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.p_1',
                    t('data_privacy.p_1'),
                    'Under the provided contact information, you can exercise the following rights at any time:'
                  )}
                </p>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.ul_1.li_1',
                      t('data_privacy.ul_1.li_1'),
                      'Information about your stored data and its processing (Art. 15 GDPR)'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.ul_1.li_2',
                      t('data_privacy.ul_1.li_2'),
                      'Correction of incorrect personal data (Art. 16 GDPR)'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.ul_1.li_3',
                      t('data_privacy.ul_1.li_3'),
                      'Deletion of your stored data (Art. 17 GDPR)'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.ul_1.li_4',
                      t('data_privacy.ul_1.li_4'),
                      'Restriction of data processing if we may not yet delete your data due to legal obligations (Art. 18 GDPR)'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.ul_1.li_5',
                      t('data_privacy.ul_1.li_5'),
                      'Objection to the processing of your data by us (Art. 21 GDPR), and Data portability, provided you have consented to data processing or have concluded a contract with us (Art. 20 GDPR)'
                    )}
                  </li>
                </ul>

                <p>
                  {translationFallback(
                    'data_privacy.p_3.text_1',
                    t('data_privacy.p_3.text_1'),
                    'Should you have given us your consent, you can revoke it at any time with effect for the future, without affecting the lawfulness of processing based on the consent before its withdrawal.'
                  )}
                  <br />
                  <br />
                  {translationFallback(
                    'data_privacy.p_3.text_2',
                    t('data_privacy.p_3.text_2'),
                    'Automated decision-making, including profiling, does not take place.'
                  )}
                  <br />
                  <br />
                  {translationFallback(
                    'data_privacy.p_3.text_3',
                    t('data_privacy.p_3.text_3'),
                    'You can lodge a complaint at any time with a supervisory authority, such as the competent supervisory authority in the federal state of your residence or the authority responsible for us as the responsible entity.'
                  )}
                  <br />
                  <br />
                  {translationFallback(
                    'data_privacy.p_3.text_4',
                    t('data_privacy.p_3.text_4'),
                    'A list of supervisory authorities (for the non-public sector) with addresses can be found at:'
                  )}
                  <a
                    className="text-white"
                    href="https://www.bfdi.bund.de/EN/Infothek/Anschriften_Links/anschriften_links-node.html"
                  >
                    https://www.bfdi.bund.de/EN/Infothek/Anschriften_Links/anschriften_links-node.html
                  </a>
                </p>

                <div className="flex flex-col gap-1">
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_1.title',
                      t('data_privacy.section_1.title'),
                      'Information about your right to object pursuant to Art. 21 GDPR - Right to object in individual cases'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_1.text_1',
                      t('data_privacy.section_1.text_1'),
                      'You have the right to object, on grounds relating to your particular situation, at any time to the processing of personal data concerning you which is based on Article 6(1)(f) GDPR (processing for the purposes of legitimate interests); this also applies to profiling based on this provision as defined in Article 4(4) GDPR.'
                    )}
                    <br />
                    <br />
                    {translationFallback(
                      'data_privacy.section_1.text_2',
                      t('data_privacy.section_1.text_2'),
                      'If you object, we will no longer process your personal data unless we can demonstrate compelling legitimate grounds for the processing which override your interests, rights, and freedoms, or the processing is for the establishment, exercise, or defense of legal claims.'
                    )}
                    <br />
                    <br />
                    {translationFallback(
                      'data_privacy.section_1.text_3',
                      t('data_privacy.section_1.text_3'),
                      'The objection should be addressed to us (contact details above).'
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_1',
                    t('data_privacy.section_2.title_1'),
                    'II. Collection of general information when visiting our website Nature and purpose of the processing:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.sectaion_2.p_4',
                    t('data_privacy.section_2.p_4'),
                    'When you access our website, i.e., if you do not register or otherwise provide information, general information of a non-personal nature is automatically collected. This information (server log files) includes the type of web browser, operating system used, domain name of your internet service provider, your IP address, and similar details.'
                  )}
                </p>

                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_5',
                    t('data_privacy.section_2.p_5'),
                    'They are processed particularly for the following purposes'
                  )}
                </p>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_2.ul_1.li_1',
                      t('data_privacy.section_2.ul_1.li_1'),
                      'Ensuring a smooth connection setup of the website,'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_2.ul_1.li_2',
                      t('data_privacy.section_2.ul_1.li_2'),
                      'Ensuring smooth use of our website,'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_2.ul_1.li_3',
                      t('data_privacy.section_2.ul_1.li_3'),
                      'Evaluating system security and stability, and'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_2.ul_1.li_4',
                      t('data_privacy.section_2.ul_1.li_4'),
                      'Optimizing our website.'
                    )}
                  </li>
                </ul>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_6',
                    t('data_privacy.section_2.p_6'),
                    'We do not use your data to draw conclusions about your person. Information of this kind is potentially evaluated statistically in an anonymized manner by us to optimize our online presence and the underlying technology.'
                  )}
                </p>
                <strong style={{ fontSize: '1.3rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_2',
                    t('data_privacy.section_2.title_2'),
                    'Legal Basis and Legitimate Interest:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_7',
                    t('data_privacy.section_2.p_7'),
                    'The processing is carried out in accordance with Art. 6 para. 1 lit. f GDPR based on our legitimate interest in improving the stability and functionality of our website.'
                  )}
                </p>
                <strong style={{ fontSize: '1.3rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_3',
                    t('data_privacy.section_2.title_3'),
                    'Processor:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_8',
                    t('data_privacy.section_2.p_8'),
                    'For certain services necessary for the operation, maintenance, development, execution, and administration of our systems, we partially transmit your data to service providers as part of data processing. A precise list of processors and their purpose is listed under VI. Where necessary, we have concluded data processing agreements with the service providers.'
                  )}
                </p>
                <strong style={{ fontSize: '1.3rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_4',
                    t('data_privacy.section_2.title_4'),
                    'Transfer to Third Countries:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_9',
                    t('data_privacy.section_2.p_9'),
                    'As part of data processing, we partially transmit your data to service providers abroad. As a suitable guarantee for the legality of data transfer, we have concluded corresponding data processing agreements and, where necessary, EU standard contracts according to Art. 46 para. 2 lit. c GDPR with the data processors.'
                  )}
                </p>
                <strong style={{ fontSize: '1.3rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_5',
                    t('data_privacy.section_2.title_5'),
                    'Storage Duration:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_10',
                    t('data_privacy.section_2.p_10'),
                    'The data is deleted as soon as it is no longer necessary for the purpose of collection. This is generally the case for data serving the provision of the website once the respective session is terminated. In the case of data stored in log files, this occurs no later than 30 days. Extended storage is possible. In this case, user IP addresses are anonymized, making it no longer possible to allocate the calling client.'
                  )}
                </p>
                <strong style={{ fontSize: '1.3rem' }}>
                  {translationFallback(
                    'data_privacy.section_2.title_6',
                    t('data_privacy.section_2.title_6'),
                    'Provision Required or Necessary:'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_2.p_11',
                    t('data_privacy.section_2.p_11'),
                    'The provision of the aforementioned personal data is neither legally nor contractually required. However, without the IP address, the service and functionality of our website cannot be guaranteed. Additionally, individual services and features may not be available or may be restricted. Therefore, objection is excluded for this reason.'
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_3.title_1',
                    t('data_privacy.section_3.title_1'),
                    'III. Registration on our Website'
                  )}
                </strong>
                <br />
                <p>
                  {translationFallback(
                    'data_privacy.section_3.p_12',
                    t('data_privacy.section_3.p_12'),
                    'Art and Purpose of Processing: For registration on our website, we require some personal data transmitted to us via an input mask. The following data is collected and possibly used for contract fulfillment: Email address, Full name, and Date of birth.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'data_privacy.section_3.p_13',
                    t('data_privacy.section_3.p_13'),
                    'At the time of registration, the following additional data is collected: Timestamp'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'data_privacy.section_3.p_14',
                    t('data_privacy.section_3.p_14'),
                    'Your registration is necessary to create a user account. This user account enables further functions related to the purchase and/or sale of spirits and wine, access to historical price and market data, or management of your own portfolio.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'data_privacy.section_3.p_15',
                    t('data_privacy.section_3.p_15'),
                    'In addition, when using Spiritory for the purchase of products, the following data is collected: Address, if applicable, phone number or mobile number, and a payment method such as a credit card. When using Spiritory for the sale of products, an IBAN must be provided, which will be used for the payout.'
                  )}
                </p>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.title_2',
                      t('data_privacy.section_3.title_2'),
                      'Legal Basis:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_16',
                      t('data_privacy.section_3.p_16'),
                      'The processing of the data entered during registration is based on the user&apos;s consent (Art. 6 para. 1 lit. a GDPR).'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.title_3',
                      t('data_privacy.section_3.title_3'),
                      'Processor:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_20',
                      t('data_privacy.section_3.p_20'),
                      'For certain services necessary for the operation, maintenance, development, execution, and administration of our systems, we partially transmit your data to service providers as part of data processing. A precise list of processors and their purpose is listed under VI. Where necessary, we have concluded data processing agreements with the service providers.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.title_4',
                      t('data_privacy.section_3.title_4'),
                      'Transfer to Third Countries:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_21',
                      t('data_privacy.section_3.p_21'),
                      'As part of data processing, we partially transmit your data to service providers abroad. As a suitable guarantee for the legality of data transfer, we have concluded corresponding data processing agreements and, where necessary, EU standard contracts according to Art. 46 para. 2 lit. c GDPR with the data processors.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.title_5',
                      t('data_privacy.section_3.title_5'),
                      'Storage Duration:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_22',
                      t('data_privacy.section_3.p_22'),
                      'We process the collected data according to Art. 6 para. 1 lit. b GDPR for the purpose of contract fulfillment.'
                    )}
                  </p>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_23',
                      t('data_privacy.section_3.p_23'),
                      'The data required for contract fulfillment are stored as a user account with us and can be deleted at any time upon request. The data will not be deleted if there are still outstanding claims to be collected after the contract has been terminated. In the case of legal retention periods, the affected data will be archived for the duration of these periods.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.title_6',
                      t('data_privacy.section_3.title_6'),
                      'Provision Required or Necessary:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.p_24',
                      t('data_privacy.section_3.p_24'),
                      'The provision of your personal data is voluntary, solely based on your consent. Without providing your personal data, we cannot grant you access to our offered content.'
                    )}
                  </p>
                </div>
                <div id="sign-in-with-google-or-apple">
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title',
                      t('data_privacy.section_3.social.title'),
                      'Sign-in with Google or Apple'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_1',
                      t('data_privacy.section_3.social.p_1'),
                      'Instead of registering with an email address and password, you can create your Spiritory account and log in with your Google account ("Sign in with Google") or your Apple ID ("Sign in with Apple"). The sign-in is handled by our authentication service provider Auth0 (see VII).'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title_data',
                      t('data_privacy.section_3.social.title_data'),
                      'Data we receive:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_2',
                      t('data_privacy.section_3.social.p_2'),
                      'We only request the basic profile information needed to create and identify your account (for Google the scopes "openid", "email" and "profile"): your name, your email address, your profile picture (Google only) and a unique account identifier. With Sign in with Apple you can choose to hide your email address; in this case we only receive an anonymous relay address from Apple. We do not receive your password and do not get access to any other data in your Google or Apple account, such as contacts, emails, calendars or files.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title_use',
                      t('data_privacy.section_3.social.title_use'),
                      'How we use this data:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_3',
                      t('data_privacy.section_3.social.p_3'),
                      'We use this data exclusively to create your Spiritory user account, to log you in securely and to contact you about your account, your purchases and your sales. We do not use data received from Google or Apple for advertising, we do not sell it and we do not use it to build advertising profiles.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title_sharing',
                      t('data_privacy.section_3.social.title_sharing'),
                      'Sharing:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_4',
                      t('data_privacy.section_3.social.p_4'),
                      'Data received from Google or Apple is only transferred to the processors listed in VII insofar as this is necessary to provide our service (in particular Auth0 for authentication), or where we are legally obliged to do so. It is not passed on to any other third parties.'
                    )}
                  </p>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_5',
                      t('data_privacy.section_3.social.p_5'),
                      "Spiritory's use and transfer of information received from Google APIs to any other app will adhere to the Google API Services User Data Policy, including the Limited Use requirements."
                    )}{' '}
                    <a
                      className="text-white"
                      href="https://developers.google.com/terms/api-services-user-data-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google API Services User Data Policy
                    </a>
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title_retention',
                      t('data_privacy.section_3.social.title_retention'),
                      'Retention and deletion:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_6',
                      t('data_privacy.section_3.social.p_6'),
                      'The data is stored for as long as your user account exists. You can request the deletion of your account and the associated data at any time by email to help@spiritory.com; statutory retention obligations (see IV) remain unaffected. You can also revoke Spiritory\'s access at any time in your Google account settings (https://myaccount.google.com/permissions) or in your Apple ID settings under "Sign in with Apple".'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_3.social.title_legal',
                      t('data_privacy.section_3.social.title_legal'),
                      'Legal basis:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_3.social.p_7',
                      t('data_privacy.section_3.social.p_7'),
                      'The processing is carried out to perform the user agreement (Art. 6(1)(b) GDPR) and on the basis of the consent you give by choosing to sign in with Google or Apple (Art. 6(1)(a) GDPR).'
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_4.title_1',
                    t('data_privacy.section_4.title_1'),
                    'IV. Provision of Paid Services'
                  )}
                </strong>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_2',
                      t('data_privacy.section_4.title_2'),
                      'Nature and Purpose of Processing:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_25',
                      t('data_privacy.section_4.p_25'),
                      'To provide paid services, the data stored under "III. Registration on our Website" is used. Additional data may be requested if not already stored in the user account to fulfill your order. The following data, in addition to order execution and contract fulfillment, are collected and used:'
                    )}
                  </p>
                  <div>
                    <strong style={{ fontSize: '1.3rem' }}>
                      {translationFallback(
                        'data_privacy.section_4.title_3',
                        t('data_privacy.section_4.title_3'),
                        'First name, Last name, if applicable, Company, Address.'
                      )}
                    </strong>
                  </div>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_4',
                      t('data_privacy.section_4.title_4'),
                      'Legal Basis:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_26',
                      t('data_privacy.section_4.p_26'),
                      'The processing of data necessary for the conclusion of the contract is based on Art. 6 para. 1 lit. b GDPR.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_5',
                      t('data_privacy.section_4.title_5'),
                      'Processor:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_27',
                      t('data_privacy.section_4.p_27'),
                      'For certain services necessary for the operation, maintenance, development, execution, and administration of our systems, we partially transmit your data to service providers as part of data processing. A precise list of processors and their purpose is listed under VI. Where necessary, we have concluded data processing agreements with the service providers.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_6',
                      t('data_privacy.section_4.title_6'),
                      'Transfer to Third Countries:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_28',
                      t('data_privacy.section_4.p_28'),
                      'As part of data processing, we partially transmit your data to service providers abroad. As a suitable guarantee for the legality of data transfer, we have concluded corresponding data processing agreements and, where necessary, EU standard contracts according to Art. 46 para. 2 lit. c GDPR with the data processors.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_7',
                      t('data_privacy.section_4.title_7'),
                      'Storage Duration:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_29',
                      t('data_privacy.section_4.p_29'),
                      'We store this data in our systems until the contract is completely fulfilled or the expiration of voucher credit and the statutory retention periods have expired. These periods are generally 6 or 10 years for proper accounting and tax requirements. In the event of a legal dispute, we only delete the data necessary for successful legal prosecution after the legal conclusion of the proceedings and complete settlement of the disputed claims; this may only occur after the expiration of the statutory retention periods on a case-by-case basis.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_4.title_8',
                      t('data_privacy.section_4.title_8'),
                      'Provision Required or Necessary:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_4.p_30',
                      t('data_privacy.section_4.p_30'),
                      'The provision of your personal data is voluntary. Without providing your personal data, we cannot grant you access to our offered content and services.'
                    )}
                  </p>
                </div>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_5.title_1',
                    t('data_privacy.section_5.title_1'),
                    'V. Contact Form'
                  )}
                </strong>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_2',
                      t('data_privacy.section_5.title_2'),
                      'Nature and Purpose of Processing:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_31',
                      t('data_privacy.section_5.p_31'),
                      'The data entered by you will be stored for the purpose of individual communication with you. For this purpose, providing a valid email address and your name is required. This is used to assign the request and subsequently answer it. Providing further data is optional.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_3',
                      t('data_privacy.section_5.title_3'),
                      'Legal Basis:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_32',
                      t('data_privacy.section_5.p_32'),
                      'The processing of data entered into the contact form is based on a legitimate interest (Art. 6 para. 1 lit. f GDPR).'
                    )}
                  </p>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_33',
                      t('data_privacy.section_5.p_33'),
                      'By providing the contact form, we aim to enable uncomplicated communication with you. The information provided by you is stored for the purpose of processing the request and for potential follow-up questions.'
                    )}
                  </p>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_34',
                      t('data_privacy.section_5.p_34'),
                      'If you contact us to request an offer, the processing of data entered into the contact form is carried out to perform pre-contractual measures (Art. 6 para. 1 lit. b GDPR).'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_4',
                      t('data_privacy.section_5.title_4'),
                      'Processor:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_35',
                      t('data_privacy.section_5.p_35'),
                      'For certain services necessary for the operation, maintenance, development, execution, and administration of our systems, we partially transmit your data to service providers as part of data processing. A precise list of processors and their purpose is listed under VI. Where necessary, we have concluded data processing agreements with the service providers.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_5',
                      t('data_privacy.section_5.title_5'),
                      'Transfer to Third Countries:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_36',
                      t('data_privacy.section_5.p_36'),
                      'As part of data processing, we partially transmit your data to service providers abroad. As a suitable guarantee for the legality of data transfer, we have concluded corresponding data processing agreements and, where necessary, EU standard contracts according to Art. 46 para. 2 lit. c GDPR with the data processors.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_6',
                      t('data_privacy.section_5.title_6'),
                      'Storage Duration:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_37',
                      t('data_privacy.section_5.p_37'),
                      'Data will be deleted at the latest 6 months after processing the request. If a contractual relationship arises, we will only delete your data after complete fulfillment of the contract and expiration of statutory retention periods.'
                    )}
                  </p>
                </div>
                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_7',
                      t('data_privacy.section_5.title_7'),
                      'Provision Required or Necessary:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_38',
                      t('data_privacy.section_5.p_38'),
                      'The provision of your personal data is voluntary. However, we can only process your request if you provide us with your name, email address, and the reason for the inquiry.'
                    )}
                  </p>
                </div>

                <div>
                  <strong style={{ fontSize: '1.3rem' }}>
                    {translationFallback(
                      'data_privacy.section_5.title_8',
                      t('data_privacy.section_5.title_8'),
                      'SSL Encryption:'
                    )}
                  </strong>
                  <p>
                    {translationFallback(
                      'data_privacy.section_5.p_39',
                      t('data_privacy.section_5.p_39'),
                      'To protect the security of your data during transmission, we use encryption methods corresponding to the current state of the art (e.g., SSL) via HTTPS.'
                    )}
                  </p>
                </div>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_6.title_1',
                    t('data_privacy.section_6.title_1'),
                    'VI. Use of Cookies'
                  )}
                </strong>

                <div className="ccm-cookie-declaration">
                  Please activate Javascript to see the list of all declared cookies and similar
                  technologies.
                </div>
              </div>

              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_7.title_1',
                    t('data_privacy.section_7.title_1'),
                    'VII. Processors'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_7.p_1',
                    t('data_privacy.section_7.p_1'),
                    'Recipients of the data are technical service providers who act as processors for the operation, maintenance, development, execution, and management of our website and voucher system.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'data_privacy.section_7.p_2',
                    t('data_privacy.section_7.p_2'),
                    'Payment processing takes place not only via banks but also via the payment service providers listed below. The collection and processing of payment data is carried out directly by the payment service provider. During the purchase process, we do not store any payment information. For the use of data by payment service providers, we refer to their terms and conditions and privacy policies.'
                  )}
                </p>

                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_1.title',
                    t('data_privacy.section_7.subsection_1.title'),
                    'Hosting and Operation'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_1.items.0',
                      t('data_privacy.section_7.subsection_1.items.0'),
                      'Microsoft Azure, Microsoft Corporation, One Microsoft Way, Redmond, WA 98052, USA, www.azure.microsoft.com'
                    )}
                  </li>
                </ul>
                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_2.title',
                    t('data_privacy.section_7.subsection_2.title'),
                    'Content Management System (CMS)'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_2.items.0',
                      t('data_privacy.section_7.subsection_2.items.0'),
                      'Strapi, Strapi SAS, 245 route des Lucioles, 06560 Valbonne, France, www.strapi.io'
                    )}
                  </li>
                </ul>
                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_3.title',
                    t('data_privacy.section_7.subsection_3.title'),
                    'Authentication'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_3.items.0',
                      t('data_privacy.section_7.subsection_3.items.0'),
                      'Auth0, Auth0 Inc. (Okta), 101 2nd Street, Suite 700, San Francisco, CA 94105, USA, www.auth0.com'
                    )}
                  </li>
                </ul>

                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_4.title',
                    t('data_privacy.section_7.subsection_4.title'),
                    'Search'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_4.items.0',
                      t('data_privacy.section_7.subsection_4.items.0'),
                      'Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany, www.hetzner.com'
                    )}
                  </li>
                </ul>

                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_5.title',
                    t('data_privacy.section_7.subsection_5.title'),
                    'Payment Processors'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_5.items.0',
                      t('data_privacy.section_7.subsection_5.items.0'),
                      'Stripe, Inc., 510 Townsend Street, San Francisco, CA 94103, USA, www.stripe.com'
                    )}
                  </li>
                </ul>
                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_6.title',
                    t('data_privacy.section_7.subsection_6.title'),
                    'Marketing'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.0',
                      t('data_privacy.section_7.subsection_6.items.0'),
                      'Klaviyo, Klaviyo, Inc., 225 Franklin St, Floor 10, Boston, MA 02110, USA, www.klaviyo.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.1',
                      t('data_privacy.section_7.subsection_6.items.1'),
                      'Karla, Gormannstrasse 19a, 10119 Berlin, Germany, www.karla.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.2',
                      t('data_privacy.section_7.subsection_6.items.2'),
                      'Google Analytics (Google Ireland Limited), Gordon House, Barrow Street, Dublin 4, Ireland, www.google.com/analytics'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.3',
                      t('data_privacy.section_7.subsection_6.items.3'),
                      'Meta Platforms Ireland Ltd., 4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Ireland, www.facebook.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.4',
                      t('data_privacy.section_7.subsection_6.items.4'),
                      'Reddit, Inc., 420 Taylor St., San Francisco, CA 94102, USA, www.reddit.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.5',
                      t('data_privacy.section_7.subsection_6.items.5'),
                      'PostHog, PostHog, Inc., 2261 Market Street #4008, San Francisco, CA 94114, USA, www.posthog.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_6.items.6',
                      t('data_privacy.section_7.subsection_6.items.6'),
                      'HubSpot, HubSpot Ireland Limited, One Dockland Central, Guild Street, Dublin 1, Ireland, www.hubspot.com'
                    )}
                  </li>
                </ul>

                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_7.title',
                    t('data_privacy.section_7.subsection_7.title'),
                    'Logistics'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_7.items.0',
                      t('data_privacy.section_7.subsection_7.items.0'),
                      'Hillebrand Gori, JF Hillebrand Group AG, Carl-Zeiss-Straße 6, 55129 Mainz, Germany, www.hillebrand.com'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_7.items.1',
                      t('data_privacy.section_7.subsection_7.items.1'),
                      'Karla, Gormannstrasse 19a, 10119 Berlin, Germany, www.karla.com'
                    )}
                  </li>
                </ul>

                <strong>
                  {translationFallback(
                    'data_privacy.section_7.subsection_8.title',
                    t('data_privacy.section_7.subsection_8.title'),
                    'Software Development'
                  )}
                </strong>
                <ul>
                  <li>
                    {translationFallback(
                      'data_privacy.section_7.subsection_8.items.0',
                      t('data_privacy.section_7.subsection_8.items.0'),
                      'Spiritory GmbH, Oberländerstr. 33, 81371 Munich'
                    )}
                  </li>
                </ul>
              </div>

              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_8.title_1',
                    t('data_privacy.section_8.title_1'),
                    'VIII. Changes to Our Privacy Policy'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_8.p_43',
                    t('data_privacy.section_8.p_43'),
                    'We reserve the right to adjust this privacy policy, including the cookie policy, to ensure it always complies with current legal requirements or to implement changes in our services in the privacy policy, such as the introduction of new services. The new privacy policy will apply to your next visit.'
                  )}
                </p>
              </div>

              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'data_privacy.section_9.title_1',
                    t('data_privacy.section_9.title_1'),
                    'IX. Questions'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'data_privacy.section_9.p_44',
                    t('data_privacy.section_9.p_44'),
                    'If you have any questions regarding privacy, please contact us using the above contact information.'
                  )}
                </p>
              </div>
            </Col>
          </Row>
        </Row>
      </Container>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps(['common', 'other', 'index']);
